import { Layout } from 'plotly.js';

export type EvidenceItem = {
  timestamp: string;
  belt_position: 'both' | 'left' | 'right' | 'null';
  health_status?: 'healthy' | 'monitor' | 'critical';
  fault_type?: string;
  source_type?: string;
  uniqueId: string;
};

export type VisualAIGraphType = 'leftRoller' | 'rightRoller' | 'rockSize';

export const levelColors = {
  healthy: {
    bg: '#11975D14', // background = 8% opacity
    line: '#11975D',
  },
  monitor: {
    bg: '#F1BF4214',
    line: '#F1BF42',
  },
  alarm: {
    bg: '#FF891B1F',
    line: '#FF891B',
  },
  critical: {
    bg: '#CC000014',
    line: '#CC0000',
  },
} as const;

export type LevelName = keyof typeof levelColors;

type Range = { start: number; end: number };

type HealthyRange = { start: number; end: number | null };
type CriticalRange = { start: number | null; end: number };

export type ShapeLevels = {
  healthy: HealthyRange;
  monitor: Range;
  alarm: Range;
  critical: CriticalRange;
};

export const buildEvidenceSvg = (isSelected: boolean) => {
  if (isSelected) {
    return `
<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path 
    d="M7.00977 1C9.01559 1 10.8694 2.0123 11.9785 3.65918L12.1904 3.99707L12.1934 4.00195C13.2668 5.8692 13.2698 8.14229 12.1924 9.99707L12.1934 9.99805C11.1157 11.8723 9.1433 13 7.00977 13C4.85889 13 2.88541 11.8742 1.80664 9.99805L1.80762 9.99707C0.730219 8.14229 0.733178 5.8692 1.80664 4.00195L1.80957 3.99707C2.88506 2.15004 4.85287 1 7.00977 1ZM6.44727 7.70996L7.60156 7L6.44727 6.28906V7.70996Z"
    fill="#7071F3"
    stroke="black"
    stroke-width="2"
  />
</svg>
`;
  }

  return `
<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5.00977 10C3.21289 10 1.57227 9.0625 0.673828 7.5C-0.224609 5.95703 -0.224609 4.0625 0.673828 2.5C1.57227 0.957031 3.21289 0 5.00977 0C6.78711 0 8.42773 0.957031 9.32617 2.5C10.2246 4.0625 10.2246 5.95703 9.32617 7.5C8.42773 9.0625 6.78711 10 5.00977 10ZM7.50977 5L3.44727 2.5V7.5L7.50977 5Z" fill="#354A64"/>
 <path
    d="M7.50977 5L3.44727 2.5V7.5L7.50977 5Z"
    fill="#FFFFFF"
    opacity="0.5"
  />
</svg>
`;
};

const svgToBase64 = (svg: string) => 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));

export const buildEvidenceMarkers = ({
  evidences,
  selectedEvidence,
  graphType,
  yRange,
  isMirrored = false,
}: {
  evidences: EvidenceItem[];
  selectedEvidence: EvidenceItem;
  graphType: VisualAIGraphType;
  yRange: { yMin: number; yMax: number };
  isMirrored: boolean;
}) => {
  const selectedId = selectedEvidence?.uniqueId;

  const filteredEvidences = evidences.filter((evidence) => {
    // Rock size → keep all evidences
    if (graphType === 'rockSize') {
      return true;
    }

    // Left roller → exclude right-only evidences
    if (graphType === 'leftRoller') {
      return evidence.belt_position !== 'right';
    }

    // Right roller → exclude left-only evidences
    if (graphType === 'rightRoller') {
      return evidence.belt_position !== 'left';
    }

    return true;
  });

  const shapes = filteredEvidences.map((e) => {
    const isSelected = e.uniqueId === selectedId;
    return {
      type: 'line',
      xref: 'x',
      yref: 'paper',
      x0: e.timestamp,
      x1: e.timestamp,
      y0: 0,
      y1: 1,
      layer: 'below',
      line: {
        color: isSelected ? '#7071F3' : '#8B97A6',
        width: 2,
        dash: isSelected ? 'solid' : '8,8',
      },
    };
  });

  const images = filteredEvidences.map((e, idx) => {
    const isSelected = e.uniqueId === selectedId;
    const yValue = isSelected ? yRange.yMax + (isMirrored ? -2 : 2) : yRange.yMax;

    return {
      name: `evidence_${idx}`,
      source: svgToBase64(buildEvidenceSvg(isSelected)),
      xref: 'x',
      yref: 'y',
      x: new Date(e.timestamp),
      y: yValue,
      sizex: 24 * 60 * 60 * 1000,
      ysizemode: 'pixel',
      sizey: isSelected ? 14 : 10,
      xanchor: 'center',
      yanchor: 'top',
      opacity: 1,
      layer: 'above',
    };
  });

  return { shapes, images };
};

// Helper to find max value from array + additional values without stack overflow
function safeMax(arr: number[], ...additionalValues: number[]): number {
  let max = -Infinity;
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] > max) max = arr[i];
  }
  for (let i = 0; i < additionalValues.length; i++) {
    if (additionalValues[i] > max) max = additionalValues[i];
  }
  return max;
}

// Helper to find min value from array + additional values without stack overflow
function safeMin(arr: number[], ...additionalValues: number[]): number {
  let min = Infinity;
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] < min) min = arr[i];
  }
  for (let i = 0; i < additionalValues.length; i++) {
    if (additionalValues[i] < min) min = additionalValues[i];
  }
  return min;
}

export type BuildLevelShapesParams = {
  levels?: ShapeLevels;
  yValues: number[];
  isMirrored: boolean;
  buffer?: number;
  shouldCalculate?: boolean;
};

export function buildLevelShapes({ levels, yValues, isMirrored = false, buffer = 30 }: BuildLevelShapesParams): {
  shapes: Layout['shapes'];
  annotations: Layout['annotations'];
} {
  if (!levels || yValues.length === 0) return { shapes: [], annotations: [] };

  const shapes: Layout['shapes'] = [];
  const annotations: Layout['annotations'] = [];

  const LEVEL_ORDER: LevelName[] = ['critical', 'alarm', 'monitor', 'healthy'];

  const { start: criticalStart, end: criticalEnd } = levels.critical;
  const { start: healthyStart, end: healthyEnd } = levels.healthy;

  const criticalHigh =
    criticalStart && criticalStart !== 0 && criticalEnd == null
      ? safeMax(yValues, criticalStart + buffer)
      : criticalEnd;
  const criticalLow = criticalStart == null ? safeMin(yValues, criticalEnd - buffer) : criticalStart;

  const healthyLow = healthyStart ?? 0;
  const healthyHigh =
    healthyStart !== 0 && healthyEnd == null ? safeMax(yValues, healthyStart + buffer) : healthyEnd ?? 0;

  const bands: Record<LevelName, { low: number; high: number }> = {
    critical: {
      low: criticalLow,
      high: criticalHigh,
    },
    alarm: {
      low: levels.alarm.start,
      high: levels.alarm.end,
    },
    monitor: {
      low: levels.monitor.start,
      high: levels.monitor.end,
    },
    healthy: {
      low: healthyLow,
      high: healthyHigh,
    },
  };

  const capitalize = (str: string) => str[0].toUpperCase() + str.slice(1);

  // Draw bands
  LEVEL_ORDER.forEach((level) => {
    const colors = levelColors[level];
    const band = bands[level];

    const y0 = Math.min(band.low, band.high);
    const y1 = Math.max(band.low, band.high);
    if (y0 === y1) return;

    // Draw rectangle
    shapes.push({
      type: 'rect',
      xref: 'paper',
      x0: 0,
      x1: 1,
      yref: 'y',
      y0,
      y1,
      fillcolor: colors.bg,
      line: { width: 0 },
      layer: 'below',
      name: `${level} zone`,
    });

    // Draw threshold line
    const lineY = isMirrored ? band.high : levels[level].start ?? band.low;
    if (lineY != null) {
      shapes.push({
        type: 'line',
        xref: 'paper',
        x0: 0,
        x1: 1,
        yref: 'y',
        y0: lineY,
        y1: lineY,
        layer: 'below',
        line: { color: colors.line, width: 1 },
      });

      annotations.push({
        xref: 'paper',
        x: 0.98,
        y: lineY,
        text: `${capitalize(level)}: ${Math.round(lineY)}`,
        showarrow: false,
        font: { color: '#55687D', size: 12 },
        xanchor: 'right',
        yanchor: 'bottom',
      });
    }
  });

  return { shapes, annotations };
}

export function getCombinedYRangeForVisualAI({
  data,
  levelShapes,
  isMirrored = false,
}: {
  data: any[];
  levelShapes: any[];
  isMirrored: boolean;
}): { yMin: number; yMax: number } {
  // Gather all Y values from the traces
  const dataPoints = data?.flatMap((trace: { y: any }) => trace.y ?? []) ?? [];
  // Get all Y values from the shapes you just built
  const shapeYValues = levelShapes.flatMap((s) => [s.y0, s.y1]).filter((v): v is number => Number.isFinite(v));

  // Combine all Y values
  const allYValues = [...dataPoints, ...shapeYValues];

  // Use safe helpers to avoid stack overflow with large datasets
  const yMax = safeMax(allYValues);
  const yMin = safeMin(allYValues);

  // If mirrored, swap min/max
  return isMirrored ? { yMin: yMax, yMax: yMin } : { yMin, yMax };
}
